import { useState, type JSX } from "react";
import { useSearchParams } from "react-router-dom";
import { Skeleton } from "../../components/dashboard/Skeleton";
import { ToolCallsPausedBanner } from "../../components/dashboard/ToolCallsPausedBanner";
import { ToolUsageChart } from "../../components/dashboard/ToolUsageChart";
import { useClaws } from "../../hooks/useClaws";
import { SUPPORT_EMAIL } from "../../config/links";
import { useToolUsage } from "../../hooks/useToolUsage";
import {
  TOOL_USAGE_RANGES,
  formatCount,
  formatResume,
  pausedState,
  windowPeriod,
  type InstanceLimit,
  type ToolUsage,
  type ToolUsageRange,
} from "../../lib/toolUsage";
import styles from "./DashboardUsage.module.css";

const LimitMeter = ({ limit, enforced }: { limit: InstanceLimit; enforced: boolean }): JSX.Element => {
  const max = limit.pausesAt ?? limit.warnsAt ?? 1;
  const pct = Math.min(100, (limit.used / max) * 100);
  const warnPct = limit.warnsAt !== null && limit.pausesAt !== null ? (limit.warnsAt / limit.pausesAt) * 100 : null;
  const paused = enforced && limit.state === "block";
  return (
    <li className={styles.meter} data-testid={`limit-${limit.window}`}>
      <div className={styles.meterHead}>
        <span className={styles.meterValue}>
          {formatCount(limit.used)}
          {limit.pausesAt !== null && <span className={styles.meterOf}> of {formatCount(limit.pausesAt)}</span>}
        </span>
        <span className={styles.meterPeriod}>{windowPeriod(limit.window)}</span>
      </div>
      <div className={styles.track} data-state={paused ? "block" : limit.state === "ok" ? "ok" : "alert"}>
        <span className={styles.fill} style={{ width: `${pct.toFixed(1)}%` }} />
        {warnPct !== null && <span className={styles.warnMark} style={{ left: `${warnPct.toFixed(1)}%` }} aria-hidden />}
      </div>
      {paused && limit.resumesAround !== null && (
        <span className={styles.meterNote}>Paused until around {formatResume(limit.resumesAround)}</span>
      )}
    </li>
  );
};

const UsageBody = ({ usage }: { usage: ToolUsage }): JSX.Element => {
  const notCounted = usage.apps.filter((a) => !a.countsTowardInstanceLimit);
  return (
    <>
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>Tool calls by app</h2>
        {usage.apps.length === 0 ? (
          <p className={styles.muted}>No tool calls in this period.</p>
        ) : (
          <ToolUsageChart usage={usage} />
        )}
      </section>

      {usage.instanceLimits.length > 0 && (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Limits</h2>
          <p className={styles.muted}>
            Limits protect you from your agent making far more calls than you intended, for example a scheduled
            task stuck in a loop, and guard against misuse. When one is reached, your agent pauses its connected
            apps until older calls fall outside that period. Chat keeps working.
            {notCounted.length > 0 &&
              ` Calls to ${notCounted.map((a) => a.name).join(", ")} don't count here; they have their own limits.`}
            {!usage.enforced.instance && " These limits are not being enforced right now."}
          </p>
          <p className={styles.muted}>
            If this is normal use for your business,{" "}
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Tool-call%20limit`}>contact support</a> and we'll review your
            limit.
          </p>
          <ul className={styles.meters}>
            {usage.instanceLimits.map((l) => (
              <LimitMeter key={l.window} limit={l} enforced={usage.enforced.instance} />
            ))}
          </ul>
        </section>
      )}

      {usage.appLimits.length > 0 && (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Apps near their own limit</h2>
          <ul className={styles.appLimits}>
            {usage.appLimits.map((l) => (
              <li key={`${l.slug}-${l.window}`}>
                <strong>{l.name}</strong>: {formatCount(l.used)}
                {l.pausesAt !== null && ` of ${formatCount(l.pausesAt)}`} in the {windowPeriod(l.window)}
                {usage.enforced.perApp && l.state === "block" && l.resumesAround !== null
                  ? `, paused until around ${formatResume(l.resumesAround)}`
                  : ""}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
};

type BodyProps = {
  tenantId: string | null;
  clawsLoading: boolean;
  status: "loading" | "ready" | "error";
  data: ToolUsage | null;
};

const UsageState = ({ tenantId, clawsLoading, status, data }: BodyProps): JSX.Element => {
  if (tenantId === null) {
    return clawsLoading ? <UsageSkeleton /> : <p className={styles.muted}>You don't have an instance yet.</p>;
  }
  if (data !== null) return <UsageBody usage={data} />;
  if (status === "error") return <p className={styles.muted}>Couldn't load usage. Try again shortly.</p>;
  return <UsageSkeleton />;
};

const UsageSkeleton = (): JSX.Element => (
  <section className={styles.card} aria-hidden>
    <Skeleton width="30%" height="0.9rem" />
    <Skeleton width="100%" height="180px" />
  </section>
);

export const DashboardUsage = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const { claws, status: clawsStatus } = useClaws();
  const [range, setRange] = useState<ToolUsageRange>("30d");
  const wanted = searchParams.get("instance");
  const tenantId =
    claws.find((c) => c.tenantId === wanted)?.tenantId ??
    claws.find((c) => c.status === "RUNNING")?.tenantId ??
    claws[0]?.tenantId ??
    null;
  const { status, data, isFetching } = useToolUsage(tenantId, range);
  const paused = data === null ? null : pausedState(data);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.eyebrow}>Usage</span>
        <h1 className={styles.title}>Tool calls</h1>
        <p className={styles.subtitle}>
          A tool call is one read or action your agent takes in a connected app, such as reading an email or
          looking up a job.
        </p>
      </header>

      {paused !== null && <ToolCallsPausedBanner paused={paused} />}

      <div className={styles.ranges} role="tablist" aria-label="Period">
        {TOOL_USAGE_RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            role="tab"
            aria-selected={range === r.key}
            className={range === r.key ? `${styles.range} ${styles.rangeActive}` : styles.range}
            onClick={() => setRange(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className={isFetching ? `${styles.stack} ${styles.fetching}` : styles.stack}>
        <UsageState tenantId={tenantId} clawsLoading={clawsStatus === "loading"} status={status} data={data} />
      </div>
    </div>
  );
};
